/**
 * Tabela de dados sobre o `Table` da Kumo, com ordenação e paginação.
 *
 * Substitui o `Table` do antd. A Kumo entrega a tabela semântica; a ordenação e a
 * paginação moram em `lib/table.ts`, como funções puras testadas — a tabela aqui só
 * as liga aos cliques.
 *
 * Em tela estreita a tabela rola na horizontal dentro do painel, em vez de espremer as
 * colunas: número quebrado em duas linhas é pior do que rolar.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Pagination, Table } from "@cloudflare/kumo";
import { ArrowsDownUpIcon, CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { nextSort, pageSlice, sortRows, type SortState, type SortValue } from "../lib/table.js";

/** Uma coluna da tabela. */
export interface Column<T> {
  readonly key: string;
  readonly title: ReactNode;
  readonly render: (row: T) => ReactNode;
  /** Presente só nas colunas ordenáveis. */
  readonly sortValue?: SortValue<T>;
  readonly align?: "left" | "right";
}

/** Entradas da tabela. */
interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string | number;
  readonly onRowClick?: (row: T) => void;
  readonly rowClassName?: (row: T) => string;
  readonly emptyText?: string;
  /** Linhas por página; ausente, a tabela mostra tudo. */
  readonly pageSize?: number;
}

/** Entradas do ícone de ordenação. */
interface SortIconProps {
  readonly state: SortState | null;
  readonly columnKey: string;
}

/** Ícone do estado de ordenação da coluna: neutro, crescente ou decrescente. */
function SortIcon({ state, columnKey }: SortIconProps): React.ReactElement {
  if (state === null || state.key !== columnKey) {
    return <ArrowsDownUpIcon className="size-3.5 opacity-50" />;
  }
  return state.direction === "asc" ? (
    <CaretUpIcon weight="fill" className="size-3.5" />
  ) : (
    <CaretDownIcon weight="fill" className="size-3.5" />
  );
}

/** Tabela ordenável e, opcionalmente, paginada. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  emptyText = "sem dados",
  pageSize,
}: DataTableProps<T>): React.ReactElement {
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    if (sort === null) {
      return [...rows];
    }
    const column = columns.find((c) => c.key === sort.key);
    return sortRows(rows, column?.sortValue, sort.direction);
  }, [rows, columns, sort]);

  const visible = pageSize === undefined ? sorted : pageSlice(sorted, page, pageSize);

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <Table>
          <Table.Header>
            <Table.Row>
              {columns.map((column) => (
                <Table.Head
                  key={column.key}
                  className={`whitespace-nowrap ${column.align === "right" ? "text-right" : ""}`}
                >
                  {column.sortValue === undefined ? (
                    column.title
                  ) : (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 transition-colors hover:text-kumo-strong"
                      onClick={() => setSort((current) => nextSort(current, column.key))}
                    >
                      {column.title}
                      <SortIcon state={sort} columnKey={column.key} />
                    </button>
                  )}
                </Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visible.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length} className="py-8 text-center text-kumo-subtle">
                  {emptyText}
                </Table.Cell>
              </Table.Row>
            ) : (
              visible.map((row) => (
                <Table.Row
                  key={rowKey(row)}
                  onClick={onRowClick === undefined ? undefined : () => onRowClick(row)}
                  className={`transition-colors ${onRowClick === undefined ? "" : "cursor-pointer hover:bg-kumo-tint"} ${rowClassName?.(row) ?? ""}`}
                >
                  {columns.map((column) => (
                    <Table.Cell
                      key={column.key}
                      className={`align-top ${column.align === "right" ? "text-right" : ""}`}
                    >
                      {column.render(row)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </div>
      {pageSize !== undefined && rows.length > pageSize && (
        <Pagination page={page} setPage={setPage} perPage={pageSize} totalCount={rows.length} />
      )}
    </div>
  );
}
